import { Context } from "csi/def/Context";
import Heading from "csi/def/Heading";
import config from "csi/reflection";
import types from "csi/types";
import { C8Node } from "csi/typings";
import { FORMAT_UNIVERSAL_DATE, FORMAT_UNIVERSAL_DATETIME } from "csi/utility/dateUtility";
import { pathCreate } from "csi/utility/pathUtility";
import DateTextBox from "csi/widget/DateTextBox";
import _WidgetBase from "dijit/_WidgetBase";
import declare from "dojo/_base/declare";
import WorkflowDate from "src/SiteAdmin/WorkflowDate";

const DURATION_UNIT_HOUR = "Wbs.DurationUnit:Hour";
const DURATION_UNIT_MINUTE = "Wbs.DurationUnit:Minute";

const WbsDate = declare(WorkflowDate, {
	/** Do not adjust timestamp */
	adjustClientServerTime: false,

	workingDaysPerWeek: config.getClassSetting("Wbs", "WorkingDaysPerWeek"),

	/**
	 * @override
	 * Returns FORMAT_UNIVERSAL_DATE if the duration unit is Day or larger, FORMAT_UNIVERSAL_DATETIME otherwise
	 */
	_getFormat(ctx: Context, h: Heading): string {
		return this.isTimeBasedDurationUnit(ctx) ? FORMAT_UNIVERSAL_DATETIME : FORMAT_UNIVERSAL_DATE;
	},

	/**
	 * @override
	 * Show either Date/Time or Date widget depending on DurationUnit
	 */
	toHtmlDynamic(ctx: Context, h: Heading, refNode: Node, params: any): _WidgetBase {
		if (this.isTimeBasedDurationUnit(ctx)) {
			return this.inherited<_WidgetBase>(arguments);
		} else {
			this._addDynamicParams(ctx, h, params);
			return new DateTextBox(params);
		}
	},

	/**
	 * @override
	 * Return value in UTC time from time dropdown control
	 */
	fromHtmlDynamic(ctx: Context, h: Heading, editNode: _WidgetBase & Element): number {
		const dt = this._getEditWidgetValue(ctx, editNode);
		return dt && (dt instanceof Date) ? types.getUTCDateTime(dt) : 0;
	},

	/** Returns the value from 'editNode' */
	_getEditWidgetValue(ctx: Context, editNode: _WidgetBase & Element): any {
		return editNode.get("value");
	},

	/**
	 * @override
	 * Setup date constraints.
	 * Restrict editing to whole hours if the duration unit is Hour
	 */
	_addDynamicParams(ctx: Context, h: Heading, params: any): void {
		this.inherited(arguments);

		const holidays: number[] = [];
		const wbs = this.getWbs(ctx);
		const holidayCalendar = ctx.cache.getNode(wbs.HolidayCalendar);

		if (holidayCalendar && holidayCalendar.IsCurrent && holidayCalendar.Active && holidayCalendar.Holidays) {
			for (const holidayCnl of holidayCalendar.Holidays) {
				const holiday = ctx.cache.getNode(holidayCnl);

				if (holiday) {
					const currentDate = new Date(holiday.StartDate);
					const endDate = new Date(holiday.EndDate);
					while (currentDate <= endDate) {
						holidays.push(currentDate.getTime());
						currentDate.setDate(currentDate.getDate() + 1);
					}
				}
			}
		}
		this._constraints["holidays"] = holidays;

		const c = params?.timeParams?.constraints;
		if (c) {
			const durationUnit = this.getDurationUnit(ctx);
			switch (durationUnit) {
				case DURATION_UNIT_HOUR: c.clickableIncrement = "T01:00:00"; break;
				case DURATION_UNIT_MINUTE: c.clickableIncrement = "T00:15:00"; break;
			}
		}

		// Returns the UTC Time without considering the time-zone of client machine
		params.value = params.value ? types.getUTCTimeDate(params.value) : null;
	},

	getWbs(ctx: Context): C8Node {
		const node = ctx.getNode();
		return node.$Type === "WbsElement" ? ctx.cache.getNode(node.WBS) : node;
	},

	/** Returns true if the duration unit is Hour or Minute, false otherwise */
	isTimeBasedDurationUnit(ctx: Context): boolean {
		const durationUnit = this.getDurationUnit(ctx);
		return (durationUnit === DURATION_UNIT_HOUR || durationUnit === DURATION_UNIT_MINUTE);
	},

	/** Returns Wbs.DurationUnit used in this WBS */
	getDurationUnit(ctx: Context): string {
		return this.getWbs(ctx).DurationUnit;
	},
});

export = WbsDate;